import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    TextField,
    Button,
    IconButton,
    Autocomplete,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { updatePolicy } from "../policies/policySlice";

const MobilePaymentEditDialog = ({ open, onClose, policy, onUpdate, userId }) => {
    const dispatch = useDispatch();
    const [formData, setFormData] = useState({
        premiumAmount: "",
        discount: "",
        paidAmount: "",
        modeOfPayment: "",
        remarks: "",
        tpPremium: "",
        odPremium: "",
        netPremium: "",
        commissionRate: "0",
        tdsRate: "5",
        totalCommission: "",
    });

    const paymentModes = ["Cash", "UPI", "UPI+Cash", "Cheque", "Online Transfer", "Online Transfer+Cash"];

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isClosing, setIsClosing] = useState(false);

    // TextField styling for mobile
    const textFieldStyles = {
        width: "100%",
        "& .MuiInputBase-root": {
            height: "44px",
            borderRadius: "8px",
            fontSize: "14px",
            backgroundColor: "#FFFFFF",
            "& fieldset": {
                borderColor: "#E0E0E0",
                borderWidth: "1px",
            },
            "&:hover fieldset": {
                borderColor: "#BDBDBD",
            },
            "&.Mui-focused fieldset": {
                borderColor: "#0063F7",
                borderWidth: "2px",
            },
        },
        "& .MuiInputBase-input": {
            padding: "10px 12px",
        },
        "& .MuiInputLabel-root": {
            fontSize: "13px",
            color: "#757575",
            "&.Mui-focused": {
                color: "#0063F7",
            },
        },
        "& .MuiInputLabel-shrink": {
            backgroundColor: "#FFFFFF",
            padding: "0 4px",
        }
    };

    useEffect(() => {
        if (policy) {
            setFormData({
                premiumAmount: policy.premiumAmount || "",
                discount: policy.discount || "0",
                paidAmount: policy.paidAmount || "",
                modeOfPayment: policy.modeOfPayment || "",
                remarks: policy.remarks || "",
                tpPremium: policy.tpPremium || "",
                odPremium: policy.odPremium || "",
                netPremium: policy.netPremium || "",
                amountPayable: policy.amountPayable || policy.premiumAmount || "",
                commissionRate: policy.commissionRate || "0",
                tdsRate: policy.tdsRate || "5",
                totalCommission: policy.totalCommission || "0",
            });
        }
    }, [policy]);

    // Auto-calculate totalCommission when payment fields change
    useEffect(() => {
        const netPremium = parseFloat(formData.netPremium) || 0;
        const payable = parseFloat(formData.amountPayable) || parseFloat(formData.premiumAmount) || netPremium || 0;
        const amountReceived = parseFloat(formData.paidAmount) || 0;
        const rate = parseFloat(formData.commissionRate) || 0;
        const tds = parseFloat(formData.tdsRate) || 0;

        // If commission rate is 0%, total commission should be 0
        let total;
        if (rate === 0) {
            total = 0;
        } else {
            // Gross Commission Total = (Net Premium * Rate) - (Net Premium - Payable)
            const grossCommissionTotal = (netPremium * (rate / 100)) - (netPremium - payable);
            // Current Gross Commission = Gross Total - (Payable - Received)
            const currentGrossCommission = grossCommissionTotal - (payable - amountReceived);
            // Total (Net) = Current Gross * (1 - TDS)
            total = currentGrossCommission * (1 - (tds / 100));
        }

        setFormData(prev => ({
            ...prev,
            totalCommission: total.toFixed(2)
        }));
    }, [formData.netPremium, formData.amountPayable, formData.paidAmount, formData.commissionRate, formData.tdsRate, formData.premiumAmount]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        let processedValue = value;

        // Apply input restrictions for amount fields
        if (['premiumAmount', 'paidAmount', 'netPremium', 'discount', 'tpPremium', 'odPremium'].includes(name)) {
            // Only allow digits and decimal point
            processedValue = value.replace(/[^0-9.]/g, '');
            // Prevent multiple decimal points
            const decimalCount = (processedValue.match(/\./g) || []).length;
            if (decimalCount > 1) {
                const parts = processedValue.split('.');
                processedValue = parts[0] + '.' + parts.slice(1).join('');
            }
            // Limit to 2 decimal places
            if (processedValue.includes('.')) {
                const parts = processedValue.split('.');
                if (parts[1] && parts[1].length > 2) {
                    processedValue = parts[0] + '.' + parts[1].substring(0, 2);
                }
            }
        }

        setFormData(prev => {
            const newData = { ...prev, [name]: processedValue };
            if (name === 'premiumAmount' || name === 'discount') {
                const total = parseFloat(newData.premiumAmount) || 0;
                const disc = parseFloat(newData.discount) || 0;
                newData.amountPayable = (total - disc).toString();
            }
            return newData;
        });
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        try {
            if (!userId) throw new Error("User ID missing");

            const updates = {
                tpPremium: formData.tpPremium,
                odPremium: formData.odPremium,
                premiumAmount: formData.premiumAmount,
                totalAmount: formData.premiumAmount,
                discount: formData.discount,
                paidAmount: formData.paidAmount,
                receivedAmount: formData.paidAmount,
                modeOfPayment: formData.modeOfPayment,
                remarks: formData.remarks,
                netPremium: formData.netPremium,
                amountPayable: formData.amountPayable,
                commissionRate: formData.commissionRate,
                tdsRate: formData.tdsRate,
                totalCommission: formData.totalCommission
            };

            await dispatch(updatePolicy({ agentId: userId, id: policy.id, updates })).unwrap();

            toast.success("Payment updated successfully", {
                containerId: "bottom-center",
                position: "bottom-center",
                autoClose: 2000,
                hideProgressBar: true,
                closeOnClick: false,
                pauseOnHover: false,
                draggable: false,
                closeButton: false,
                style: {
                    backgroundColor: '#166534', // Dark green theme
                    color: '#FFFFFF',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: 500,
                    padding: '10px 20px',
                    minHeight: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    marginBottom: '100px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    width: 'auto',
                    maxWidth: '90vw',
                },
                icon: false,
            });

            if (onUpdate) onUpdate();
            handleClose();
        } catch (err) {
            console.error(err);
            toast.error("Failed to update payment.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setIsClosing(true);
        setTimeout(() => {
            setIsClosing(false);
            onClose();
        }, 200);
    };

    // Prevent background scroll when open
    useEffect(() => {
        if (open) {
            const scrollY = window.scrollY;
            document.body.style.position = 'fixed';
            document.body.style.top = `-${scrollY}px`;
            document.body.style.width = '100%';
            document.body.style.overflow = 'hidden';
        } else {
            const scrollY = document.body.style.top;
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.width = '';
            document.body.style.overflow = 'unset';
            if (scrollY) {
                window.scrollTo(0, parseInt(scrollY || '0') * -1);
            }
        }
        return () => {
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.width = '';
            document.body.style.overflow = 'unset';
        };
    }, [open]);

    // Click outside to close
    useEffect(() => {
        if (!open) return;

        const handleClickOutside = (e) => {
            const filterBox = document.querySelector('[data-payment-edit-box]');

            // Check if click is on Autocomplete dropdown (MUI poppers/listbox)
            const isPopper = e.target.closest('.MuiAutocomplete-popper') ||
                e.target.closest('.MuiAutocomplete-listbox');

            if (filterBox && !filterBox.contains(e.target) && !isPopper) {
                handleClose();
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [open]);

    if (!open && !isClosing) return null;

    return (
        <Box
            data-payment-edit-box
            sx={{
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: isClosing
                    ? 'translate(-50%, -50%) scale(0.3)'
                    : 'translate(-50%, -50%) scale(1)',
                backgroundColor: 'white',
                borderRadius: '12px',
                boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                zIndex: 1300,
                width: '92vw',
                maxWidth: 400,
                maxHeight: '90vh',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                animation: isClosing
                    ? 'collapseScale 0.2s ease-in forwards'
                    : 'expandScale 0.3s ease-out',
                '@keyframes expandScale': {
                    '0%': {
                        opacity: 0,
                        transform: 'translate(-50%, -50%) scale(0.3)',
                    },
                    '100%': {
                        opacity: 1,
                        transform: 'translate(-50%, -50%) scale(1)',
                    }
                },
                '@keyframes collapseScale': {
                    '0%': {
                        opacity: 1,
                        transform: 'translate(-50%, -50%) scale(1)',
                    },
                    '100%': {
                        opacity: 0,
                        transform: 'translate(-50%, -50%) scale(0.3)',
                    }
                }
            }}
        >
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%'
            }}>
                {/* Header */}
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 2,
                    pb: 1
                }}>
                    <Typography
                        variant="h9"
                        sx={{
                            fontFamily: 'Roboto Slab',
                            fontWeight: 500,
                            fontSize: { xs: '1.1rem', sm: '1.375rem', md: '1.5rem' },
                            lineHeight: { xs: '1.5', sm: '1.6' },
                            letterSpacing: '1%',
                            color: '#212121',
                        }}
                    >
                        Edit Payment
                    </Typography>
                    <IconButton
                        onClick={handleClose}
                        size="small"
                        sx={{
                            color: '#666',
                            '&:hover': { backgroundColor: '#f5f5f5' }
                        }}
                    >
                        <CloseIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Box>

                {/* Content - SCROLLABLE AREA */}
                <Box sx={{
                    p: 2,
                    pt: 0.5,
                    overflowY: 'auto',
                    flex: 1,
                    WebkitOverflowScrolling: 'touch',
                    '&::-webkit-scrollbar': {
                        width: '4px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: '#E0E0E0',
                        borderRadius: '4px',
                    }
                }}>
                    {/* Payment Details Section */}
                    <Box sx={{ mb: 2.5 }}>
                        <Typography sx={{
                            variant: "h9",
                            fontFamily: 'Roboto',
                            fontSize: '14px',
                            fontWeight: 600,
                            color: '#212121',
                            mb: 2.2
                        }}>
                            Payment Details
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {policy?.policyCategory === "motor" && (
                                <>
                                    {policy.policyType !== "Liability" && (
                                        <TextField
                                            label="OD Premium"
                                            name="odPremium"
                                            value={formData.odPremium}
                                            onChange={handleChange}
                                            size="small"
                                            sx={textFieldStyles}
                                        />
                                    )}
                                    {(policy.policyType === "Package" || policy.policyType === "Liability") && (
                                        <TextField
                                            label="TP Premium"
                                            name="tpPremium"
                                            value={formData.tpPremium}
                                            onChange={handleChange}
                                            size="small"
                                            sx={textFieldStyles}
                                        />
                                    )}
                                </>
                            )}
                            <TextField
                                label="Net Premium"
                                name="netPremium"
                                value={formData.netPremium}
                                onChange={handleChange}
                                size="small"
                                sx={textFieldStyles}
                            />
                            <TextField
                                label="Total Premium"
                                name="premiumAmount"
                                value={formData.premiumAmount}
                                onChange={handleChange}
                                size="small"
                                sx={textFieldStyles}
                            />
                            <TextField
                                label="Customer Discount"
                                name="discount"
                                value={formData.discount}
                                onChange={handleChange}
                                size="small"
                                sx={textFieldStyles}
                            />
                            <TextField
                                label="Amount Payable"
                                name="amountPayable"
                                value={formData.amountPayable}
                                onChange={handleChange}
                                size="small"
                                sx={textFieldStyles}
                                InputProps={{
                                    readOnly: true,
                                }}
                            />
                            <TextField
                                label="Amount Received"
                                name="paidAmount"
                                value={formData.paidAmount}
                                onChange={handleChange}
                                size="small"
                                sx={textFieldStyles}
                            />
                            <Autocomplete
                                options={paymentModes}
                                value={formData.modeOfPayment}
                                disableClearable
                                onChange={(event, newValue) => {
                                    setFormData(prev => ({ ...prev, modeOfPayment: newValue || "" }));
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Payment Mode"
                                        size="small"
                                        sx={textFieldStyles}
                                    />
                                )}
                                componentsProps={{
                                    paper: {
                                        sx: {
                                            borderRadius: '8px',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                            '& .MuiAutocomplete-listbox': {
                                                padding: '4px',
                                                '& .MuiAutocomplete-option': {
                                                    fontSize: '14px',
                                                    padding: '8px 12px',
                                                    borderRadius: '4px',
                                                }
                                            }
                                        }
                                    }
                                }}
                            />
                            <TextField
                                label="Remarks"
                                name="remarks"
                                value={formData.remarks}
                                onChange={handleChange}
                                size="small"
                                multiline
                                rows={2}
                                sx={{
                                    ...textFieldStyles,
                                    "& .MuiInputBase-root": {
                                        ...textFieldStyles["& .MuiInputBase-root"],
                                        height: "auto",
                                        minHeight: "80px",
                                        padding: "8px 0"
                                    }
                                }}
                            />
                        </Box>
                    </Box>
                </Box>

                {/* Footer Buttons */}
                <Box sx={{
                    px: 2,
                    py: 1.5,
                    backgroundColor: '#FFFFFF',
                    borderTop: '1px solid #f0f0f0',
                    display: 'flex',
                    gap: 1.5
                }}>
                    <Button
                        onClick={handleClose}
                        variant="outlined"
                        sx={{
                            flex: 1,
                            height: '40px',
                            borderRadius: '8px',
                            borderColor: '#B00020',
                            color: '#B00020',
                            fontFamily: 'Roboto',
                            fontWeight: 500,
                            fontSize: '14px',
                            textTransform: 'none',
                            "&:hover": {
                                borderColor: '#B00020',
                                backgroundColor: 'rgba(176, 0, 32, 0.04)'
                            }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        sx={{
                            flex: 1,
                            height: '40px',
                            borderRadius: '8px',
                            backgroundColor: isSubmitting ? '#7e9ed8' : '#0063F7 !important',
                            color: '#FFFFFF',
                            fontFamily: 'Roboto',
                            fontWeight: 500,
                            fontSize: '14px',
                            textTransform: 'none',
                            "&:hover:not(:disabled)": {
                                backgroundColor: '#0051d4 !important',
                            },
                            "&:disabled": {
                                backgroundColor: '#7e9ed8 !important',
                                color: '#FFFFFF'
                            }
                        }}
                    >
                        {isSubmitting ? "Updating..." : "Update"}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

export default MobilePaymentEditDialog;
